import * as React from 'react';
import { ITelemetry, useTelemetry } from '@iamexperiences/ecos-telemetry';
import { ErrorLine } from '@uifabric/portal-ux/lib/ErrorLine';
import { LoadingLine } from '@uifabric/portal-ux/lib/LoadingLine';
import { useLocalization } from '@microsoft/portal-app/lib/shared/localization';
import { useQos } from '@iamexperiences/ecos-telemetry';
import { useEffect } from 'react';

import {
  IRegionalShortDateTimeFormatData,
  useRegionalShortDateTimeFormatData,
} from '../hooks/useRegionalShortDateTimeFormatData';
import { IAgreementAcceptance } from '../models/types';
import { TermsOfUseCardAuth } from '../shared/TermsOfUseCardAuth';
import { classNames } from '../shared/TermsOfUse.styles';
import { convertDateTimeFormat } from '../utilities/convertDateTimeFormat';
import { useGetMyAcceptances } from '../hooks/useGetMyAcceptances';

export interface IAcceptanceListProps {
  readonly orgName: string | null;
}

export const AcceptanceList: React.FC<IAcceptanceListProps> = ({ orgName }) => {
  const telemetry = useTelemetry();
  const [t] = useLocalization('TermsOfUse');
  const { reportLoadComplete, reportFailure } = useQos('AcceptanceList');

  const getMyAcceptancesQuery = useGetMyAcceptances();
  const regionalShortDateTimeFormatData = useRegionalShortDateTimeFormatData();

  useEffect(() => {
    if (getMyAcceptancesQuery.isError) {
      reportFailure(getMyAcceptancesQuery.error);
    } else if (!getMyAcceptancesQuery.isLoading) {
      reportLoadComplete();
    }
  }, [reportLoadComplete, reportFailure, getMyAcceptancesQuery]);

  const subtitle = () => {
    if (getMyAcceptancesQuery.data?.length === 0) {
      return (
        <div id={'noMyAcceptances'} className={classNames.subtitleClass}>
          {t('noMyAcceptances', { orgName })}
        </div>
      );
    } else if (getMyAcceptancesQuery.data) {
      return (
        <div id={'myAcceptancesSubHeading'} className={classNames.subtitleClass}>
          {t('myAcceptancesSubHeading')}
        </div>
      );
    } else if (getMyAcceptancesQuery.isError) {
      return <ErrorLine>{t('messageLoadMyAcceptancesError')}</ErrorLine>;
    } else {
      return <LoadingLine />;
    }
  };

  // Sort the acceptances in chronologically descending order
  const cards =
    getMyAcceptancesQuery.data
      ?.sort((a, b) => {
        return Date.parse(b['createdDateTime']) - Date.parse(a['createdDateTime']);
      })
      .map((acceptance, index) => getAcceptanceCard(telemetry, acceptance, index, regionalShortDateTimeFormatData)) || null;

  return (
    <>
      {subtitle()}
      {cards}
    </>
  );
};

const getAcceptanceCard = (
  telemetry: ITelemetry,
  acceptance: IAgreementAcceptance,
  index: number,
  regionalShortDateTimeFormatData: IRegionalShortDateTimeFormatData,
) => {
  const acceptanceDate = convertDateTimeFormat({
    telemetry: telemetry,
    dateTime: acceptance.createdDateTime,
    timezonePreferenceOffset: regionalShortDateTimeFormatData.timezonePreferenceOffset,
    dateFormat: regionalShortDateTimeFormatData.dateFormat,
    timeFormat: regionalShortDateTimeFormatData.timeFormat,
  });
  const acceptanceCardId = `acceptance${index}`;

  return (
    <TermsOfUseCardAuth
      key={acceptanceCardId}
      agreementId={acceptance.agreementId}
      policyFileId={acceptance.policyFileId}
      acceptanceDate={acceptanceDate}
      deviceDisplayName={acceptance.deviceDisplayName}
    />
  );
};
